import { useState, type FormEvent } from 'react'
import { alsDatum, api } from '../../api/client'
import {
  ABWESENHEITSARTEN,
  ABWESENHEIT_TEXT,
  QUALIFIKATIONSTYPEN,
  STATUS_ETIKETT,
  STATUS_TEXT,
  fristKlasse,
  type Fahrer,
  type Kursberechtigung,
  type Qualifikation,
  type Team,
} from './typen'

interface Eigenschaften {
  fahrer: Fahrer | null
  teams: Team[]
  betriebshoefe: { id: number; name: string }[]
  kursnummern: string[]
  onGespeichert: (fahrerId: number | null) => void
  onAbbruch: () => void
}

const LEER: Fahrer = {
  id: 0,
  personalnummer: '',
  vorname: '',
  nachname: '',
  geburtsdatum: null,
  eintritt: null,
  austritt: null,
  email: null,
  telefon: null,
  betriebshofId: null,
  betriebshofName: null,
  teamId: null,
  teamName: null,
  teamFarbe: null,
  wochenarbeitszeitMinuten: 2340,
  aktiv: true,
  istFahrer: true,
  einsatzbereit: true,
  heuteAbwesend: false,
  qualifikationen: [],
  kursberechtigungen: [],
  abwesenheiten: [],
  einsetzbarAufKursen: [],
}

export function FahrerFormular({
  fahrer,
  teams,
  betriebshoefe,
  kursnummern,
  onGespeichert,
  onAbbruch,
}: Eigenschaften) {
  const istNeu = fahrer === null
  const [daten, setDaten] = useState<Fahrer>(fahrer ?? { ...LEER })
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [neueAbwesenheit, setNeueAbwesenheit] = useState({
    art: 'URLAUB',
    von: '',
    bis: '',
    status: '',
    bemerkung: '',
  })

  const team = teams.find((t) => t.id === daten.teamId)
  const teamKurse = team?.kursnummern ?? []
  const springer = daten.kursberechtigungen.filter((k) => k.art === 'SPRINGER')
  const gesperrt = daten.kursberechtigungen.filter((k) => k.art === 'GESPERRT')

  // Vorschau der Regel: Team-Kurse ∪ Springer \ Gesperrt
  const effektiv = [
    ...new Set([...teamKurse, ...springer.map((k) => k.kursnummer)]),
  ].filter((k) => !gesperrt.some((g) => g.kursnummer === k))
  effektiv.sort((a, b) => a.localeCompare(b, 'de', { numeric: true }))

  async function speichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    setFehler(null)
    setLaeuft(true)
    try {
      const koerper = {
        personalnummer: daten.personalnummer,
        vorname: daten.vorname,
        nachname: daten.nachname,
        geburtsdatum: daten.geburtsdatum || null,
        eintritt: daten.eintritt || null,
        austritt: daten.austritt || null,
        email: daten.email || null,
        telefon: daten.telefon || null,
        betriebshofId: daten.betriebshofId,
        teamId: daten.teamId,
        wochenarbeitszeitMinuten: daten.wochenarbeitszeitMinuten,
        aktiv: daten.aktiv,
        istFahrer: daten.istFahrer,
        qualifikationen: daten.qualifikationen.map((q) => ({
          id: q.id,
          typ: q.typ,
          nummer: q.nummer || null,
          gueltigVon: q.gueltigVon || null,
          gueltigBis: q.gueltigBis || null,
          bemerkung: q.bemerkung || null,
        })),
        kursberechtigungen: daten.kursberechtigungen.map((k) => ({
          id: k.id,
          kursnummer: k.kursnummer,
          art: k.art,
          eingewiesenAm: k.eingewiesenAm || null,
          bemerkung: k.bemerkung || null,
        })),
      }
      const gespeichert = istNeu
        ? await api.post<{ id: number }>('/api/fahrpersonal', koerper)
        : await api.put<{ id: number }>(`/api/fahrpersonal/${daten.id}`, koerper)
      onGespeichert(daten.austritt ? gespeichert.id : null)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen')
    } finally {
      setLaeuft(false)
    }
  }

  function qualifikationHinzu() {
    const vorhanden = new Set(daten.qualifikationen.map((q) => q.typ))
    const naechster = QUALIFIKATIONSTYPEN.find((t) => !vorhanden.has(t.wert))
    if (!naechster) return
    const neu: Qualifikation = {
      id: null,
      typ: naechster.wert,
      nummer: null,
      gueltigVon: null,
      gueltigBis: null,
      tageBisAblauf: null,
      gueltig: true,
      pflicht: naechster.pflicht,
      bemerkung: null,
    }
    setDaten({ ...daten, qualifikationen: [...daten.qualifikationen, neu] })
  }

  function kursHinzu(art: 'SPRINGER' | 'GESPERRT', kursnummer: string) {
    if (!kursnummer) return
    if (daten.kursberechtigungen.some((k) => k.kursnummer === kursnummer && k.art === art)) return
    const neu: Kursberechtigung = {
      id: null,
      kursnummer,
      art,
      eingewiesenAm: null,
      bemerkung: null,
    }
    setDaten({ ...daten, kursberechtigungen: [...daten.kursberechtigungen, neu] })
  }

  function kursWeg(eintrag: Kursberechtigung) {
    setDaten({
      ...daten,
      kursberechtigungen: daten.kursberechtigungen.filter((k) => k !== eintrag),
    })
  }

  async function abwesenheitAnlegen() {
    if (!neueAbwesenheit.von || !neueAbwesenheit.bis) {
      setFehler('Bitte Zeitraum angeben')
      return
    }
    try {
      await api.post(`/api/fahrpersonal/${daten.id}/abwesenheiten`, {
        art: neueAbwesenheit.art,
        von: neueAbwesenheit.von,
        bis: neueAbwesenheit.bis,
        status: neueAbwesenheit.status || null,
        bemerkung: neueAbwesenheit.bemerkung || null,
      })
      const frisch = await api.get<Fahrer>(`/api/fahrpersonal/${daten.id}`)
      setDaten(frisch)
      setNeueAbwesenheit({ art: 'URLAUB', von: '', bis: '', status: '', bemerkung: '' })
      setFehler(null)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Anlegen fehlgeschlagen')
    }
  }

  async function abwesenheitStatus(id: number, status: string) {
    const a = daten.abwesenheiten.find((x) => x.id === id)
    if (!a) return
    await api.put(`/api/fahrpersonal/abwesenheiten/${id}`, {
      art: a.art,
      von: a.von,
      bis: a.bis,
      status,
      bemerkung: a.bemerkung,
    })
    setDaten(await api.get<Fahrer>(`/api/fahrpersonal/${daten.id}`))
  }

  async function abwesenheitWeg(id: number) {
    if (!confirm('Abwesenheit löschen?')) return
    await api.loeschen(`/api/fahrpersonal/abwesenheiten/${id}`)
    setDaten(await api.get<Fahrer>(`/api/fahrpersonal/${daten.id}`))
  }

  const raster = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 14,
  } as const

  return (
    <div className="karte abschnitt">
      <h2>{istNeu ? 'Neuer Mitarbeiter' : `${daten.nachname}, ${daten.vorname}`}</h2>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <form onSubmit={speichern}>
        <h3 style={{ margin: '16px 0 10px' }}>Stammdaten</h3>
        <div style={raster}>
          <div className="feld">
            <label>Personalnummer *</label>
            <input
              type="text"
              required
              value={daten.personalnummer}
              onChange={(e) => setDaten({ ...daten, personalnummer: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>Vorname *</label>
            <input
              type="text"
              required
              value={daten.vorname}
              onChange={(e) => setDaten({ ...daten, vorname: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>Nachname *</label>
            <input
              type="text"
              required
              value={daten.nachname}
              onChange={(e) => setDaten({ ...daten, nachname: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>Geburtsdatum</label>
            <input
              type="date"
              value={daten.geburtsdatum ?? ''}
              onChange={(e) => setDaten({ ...daten, geburtsdatum: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>Eintritt</label>
            <input
              type="date"
              value={daten.eintritt ?? ''}
              onChange={(e) => setDaten({ ...daten, eintritt: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>Austritt</label>
            <input
              type="date"
              value={daten.austritt ?? ''}
              onChange={(e) => setDaten({ ...daten, austritt: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>E-Mail</label>
            <input
              type="email"
              value={daten.email ?? ''}
              onChange={(e) => setDaten({ ...daten, email: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>Telefon</label>
            <input
              type="text"
              value={daten.telefon ?? ''}
              onChange={(e) => setDaten({ ...daten, telefon: e.target.value })}
            />
          </div>
          <div className="feld">
            <label>Team</label>
            <select
              value={daten.teamId ?? ''}
              onChange={(e) =>
                setDaten({ ...daten, teamId: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">— ohne Team —</option>
              {teams
                .filter((t) => t.aktiv)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.kursnummern.length} Kurse)
                  </option>
                ))}
            </select>
          </div>
          <div className="feld">
            <label>Betriebshof</label>
            <select
              value={daten.betriebshofId ?? ''}
              onChange={(e) =>
                setDaten({
                  ...daten,
                  betriebshofId: e.target.value ? Number(e.target.value) : null,
                })
              }
            >
              <option value="">—</option>
              {betriebshoefe.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label>Wochenarbeitszeit (Minuten)</label>
            <input
              type="number"
              value={daten.wochenarbeitszeitMinuten ?? ''}
              onChange={(e) =>
                setDaten({
                  ...daten,
                  wochenarbeitszeitMinuten: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </div>
          <div className="feld">
            <label>Status</label>
            <select
              value={daten.aktiv ? 'ja' : 'nein'}
              onChange={(e) => setDaten({ ...daten, aktiv: e.target.value === 'ja' })}
            >
              <option value="ja">beschäftigt</option>
              <option value="nein">ausgeschieden</option>
            </select>
          </div>
          <div className="feld">
            <label>Tätigkeit</label>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, marginBottom: 0 }}
            >
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={daten.istFahrer}
                onChange={(e) => setDaten({ ...daten, istFahrer: e.target.checked })}
              />
              Fahrer
            </label>
            <div style={{ color: 'var(--grau-500)', fontSize: '0.8rem', marginTop: 4 }}>
              Ohne Haken: Werkstatt oder Büro — steht in der Personalliste, aber nicht in
              Dienstplan, Disposition und Vertretungssuche.
            </div>
          </div>
        </div>

        {/* ---------------- Qualifikationen ---------------- */}
        <h3 style={{ margin: '22px 0 10px' }}>Qualifikationen</h3>
        <div className="tabelle-huelle" style={{ marginBottom: 10 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Art</th>
                <th>Nummer</th>
                <th style={{ width: 160 }}>Gültig bis</th>
                <th style={{ width: 120 }}>Zustand</th>
                <th style={{ width: 50 }} />
              </tr>
            </thead>
            <tbody>
              {daten.qualifikationen.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--grau-500)' }}>
                    Noch keine Qualifikationen erfasst
                  </td>
                </tr>
              ) : (
                daten.qualifikationen.map((q, i) => (
                  <tr key={q.id ?? `neu-${i}`}>
                    <td>
                      <select
                        value={q.typ}
                        onChange={(e) => {
                          const kopie = [...daten.qualifikationen]
                          kopie[i] = { ...q, typ: e.target.value }
                          setDaten({ ...daten, qualifikationen: kopie })
                        }}
                      >
                        {QUALIFIKATIONSTYPEN.map((t) => (
                          <option key={t.wert} value={t.wert}>
                            {t.text}
                            {t.pflicht ? ' *' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={q.nummer ?? ''}
                        onChange={(e) => {
                          const kopie = [...daten.qualifikationen]
                          kopie[i] = { ...q, nummer: e.target.value }
                          setDaten({ ...daten, qualifikationen: kopie })
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={q.gueltigBis ?? ''}
                        onChange={(e) => {
                          const kopie = [...daten.qualifikationen]
                          kopie[i] = { ...q, gueltigBis: e.target.value }
                          setDaten({ ...daten, qualifikationen: kopie })
                        }}
                      />
                    </td>
                    <td>
                      {q.id !== null && (
                        <span className={`etikett ${fristKlasse(q.gueltig, q.tageBisAblauf)}`}>
                          {!q.gueltig
                            ? 'abgelaufen'
                            : q.tageBisAblauf === null
                              ? 'unbefristet'
                              : `${q.tageBisAblauf} Tage`}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="knopf-schlicht"
                        style={{ padding: '4px 9px', color: 'var(--rot-500)' }}
                        onClick={() =>
                          setDaten({
                            ...daten,
                            qualifikationen: daten.qualifikationen.filter((_, j) => j !== i),
                          })
                        }
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <button type="button" className="knopf-schlicht" onClick={qualifikationHinzu}>
          Qualifikation hinzufügen
        </button>
        <p style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginTop: 8 }}>
          Mit * gekennzeichnete Qualifikationen sind Pflicht — läuft eine ab, ist der Mitarbeiter
          gesperrt.
        </p>

        {/* ---------------- Kurseinsatz ---------------- */}
        <h3 style={{ margin: '22px 0 10px' }}>Kurseinsatz</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          <div>
            <label>Über das Team</label>
            <div className="karte" style={{ padding: 12, boxShadow: 'none', background: 'var(--grau-50)' }}>
              {team ? (
                teamKurse.length === 0 ? (
                  <span style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
                    Dem Team {team.name} sind keine Kurse zugeordnet
                  </span>
                ) : (
                  teamKurse.map((k) => (
                    <span key={k} className="etikett navy" style={{ marginRight: 5 }}>
                      {k}
                    </span>
                  ))
                )
              ) : (
                <span style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
                  Kein Team zugeordnet
                </span>
              )}
            </div>
          </div>

          <div>
            <label>Zusätzlich als Springer</label>
            <div className="karte" style={{ padding: 12, boxShadow: 'none', background: 'var(--grau-50)' }}>
              {springer.map((k) => (
                <span
                  key={k.kursnummer}
                  className="etikett gruen"
                  style={{ marginRight: 5, cursor: 'pointer' }}
                  title="Entfernen"
                  onClick={() => kursWeg(k)}
                >
                  {k.kursnummer} ×
                </span>
              ))}
              <select
                style={{ marginTop: 8 }}
                value=""
                onChange={(e) => kursHinzu('SPRINGER', e.target.value)}
              >
                <option value="">+ Kurs hinzufügen …</option>
                {kursnummern
                  .filter((k) => !teamKurse.includes(k))
                  .filter((k) => !springer.some((s) => s.kursnummer === k))
                  .map((k) => (
                    <option key={k} value={k}>
                      Kurs {k}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label>Gesperrt</label>
            <div className="karte" style={{ padding: 12, boxShadow: 'none', background: 'var(--grau-50)' }}>
              {gesperrt.map((k) => (
                <span
                  key={k.kursnummer}
                  className="etikett rot"
                  style={{ marginRight: 5, cursor: 'pointer' }}
                  title="Entfernen"
                  onClick={() => kursWeg(k)}
                >
                  {k.kursnummer} ×
                </span>
              ))}
              <select
                style={{ marginTop: 8 }}
                value=""
                onChange={(e) => kursHinzu('GESPERRT', e.target.value)}
              >
                <option value="">+ Kurs sperren …</option>
                {[...new Set([...teamKurse, ...springer.map((s) => s.kursnummer)])]
                  .filter((k) => !gesperrt.some((g) => g.kursnummer === k))
                  .map((k) => (
                    <option key={k} value={k}>
                      Kurs {k}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        <div className="meldung erfolg" style={{ marginTop: 14 }}>
          <strong>Einsetzbar auf {effektiv.length} Kursen:</strong>{' '}
          {effektiv.length === 0 ? 'keine' : effektiv.join(', ')}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button type="submit" className="knopf-primaer" disabled={laeuft}>
            {laeuft ? 'Wird gespeichert …' : 'Speichern'}
          </button>
          <button type="button" className="knopf-schlicht" onClick={onAbbruch}>
            Abbrechen
          </button>
        </div>
      </form>

      {/* ---------------- Abwesenheiten ---------------- */}
      {!istNeu && (
        <>
          <h3 style={{ margin: '26px 0 10px' }}>Abwesenheiten</h3>
          <div className="tabelle-huelle" style={{ marginBottom: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Art</th>
                  <th>Zeitraum</th>
                  <th>Tage</th>
                  <th>Status</th>
                  <th>Bemerkung</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {daten.abwesenheiten.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ color: 'var(--grau-500)' }}>
                      Keine Abwesenheiten erfasst
                    </td>
                  </tr>
                ) : (
                  daten.abwesenheiten.map((a) => (
                    <tr key={a.id}>
                      <td>{ABWESENHEIT_TEXT[a.art] ?? a.art}</td>
                      <td className="zahl">
                        {alsDatum(a.von)} – {alsDatum(a.bis)}
                      </td>
                      <td className="zahl">{a.tage}</td>
                      <td>
                        <span className={`etikett ${STATUS_ETIKETT[a.status] ?? 'grau'}`}>
                          {STATUS_TEXT[a.status] ?? a.status}
                        </span>
                      </td>
                      <td style={{ color: 'var(--grau-500)', fontSize: '0.83rem' }}>
                        {a.bemerkung}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {a.status === 'BEANTRAGT' && (
                          <>
                            <button
                              type="button"
                              className="knopf-schlicht"
                              style={{ padding: '4px 10px' }}
                              onClick={() => void abwesenheitStatus(a.id, 'GENEHMIGT')}
                            >
                              Genehmigen
                            </button>{' '}
                            <button
                              type="button"
                              className="knopf-schlicht"
                              style={{ padding: '4px 10px' }}
                              onClick={() => void abwesenheitStatus(a.id, 'ABGELEHNT')}
                            >
                              Ablehnen
                            </button>{' '}
                          </>
                        )}
                        <button
                          type="button"
                          className="knopf-schlicht"
                          style={{ padding: '4px 9px', color: 'var(--rot-500)' }}
                          onClick={() => void abwesenheitWeg(a.id)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="feld" style={{ marginBottom: 0 }}>
              <label>Art</label>
              <select
                value={neueAbwesenheit.art}
                onChange={(e) => setNeueAbwesenheit({ ...neueAbwesenheit, art: e.target.value })}
              >
                {ABWESENHEITSARTEN.map((a) => (
                  <option key={a.wert} value={a.wert}>
                    {a.text}
                  </option>
                ))}
              </select>
            </div>
            <div className="feld" style={{ marginBottom: 0 }}>
              <label>Von</label>
              <input
                type="date"
                value={neueAbwesenheit.von}
                onChange={(e) => setNeueAbwesenheit({ ...neueAbwesenheit, von: e.target.value })}
              />
            </div>
            <div className="feld" style={{ marginBottom: 0 }}>
              <label>Bis</label>
              <input
                type="date"
                value={neueAbwesenheit.bis}
                onChange={(e) => setNeueAbwesenheit({ ...neueAbwesenheit, bis: e.target.value })}
              />
            </div>
            <div className="feld" style={{ marginBottom: 0, flex: 1, minWidth: 160 }}>
              <label>Bemerkung</label>
              <input
                type="text"
                value={neueAbwesenheit.bemerkung}
                onChange={(e) =>
                  setNeueAbwesenheit({ ...neueAbwesenheit, bemerkung: e.target.value })
                }
              />
            </div>
            <button type="button" className="knopf-akzent" onClick={() => void abwesenheitAnlegen()}>
              Eintragen
            </button>
          </div>
          <p style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginTop: 8 }}>
            Eine Krankmeldung gilt sofort. Urlaub wird zunächst beantragt — blockiert den Einsatz
            aber bereits, damit niemand versehentlich verplant wird.
          </p>
        </>
      )}
    </div>
  )
}
